import { Facebook, Instagram, Linkedin, Mail, MessageCircle, Phone, Youtube } from "lucide-react";
import { useTranslation } from "react-i18next";
import { FooterLink, type FooterLinkItem } from "./FooterLink";

export interface FooterSocial {
  label: string;
  href: string;
  icon: typeof Instagram;
}

export interface FooterContactColumnProps {
  title: string;
  channels: readonly FooterLinkItem[];
  socials: readonly FooterSocial[];
}

/** Los canales de contacto por defecto. Datos, no JSX. */
export const DEFAULT_CONTACT_CHANNELS: readonly FooterLinkItem[] = [
  {
    label: "Correo",
    literal: "hola@itsthebest1111.com",
    to: "mailto:hola@itsthebest1111.com",
    external: true,
    icon: <Mail className="h-3.5 w-3.5 shrink-0 text-ds-violet-pop" aria-hidden="true" />,
  },
  {
    label: "WhatsApp · soporte",
    to: "https://wa.me/525533851066",
    external: true,
    icon: (
      <MessageCircle className="h-3.5 w-3.5 shrink-0 text-ds-violet-pop" aria-hidden="true" />
    ),
  },
  {
    label: "Teléfono México",
    literal: "🇲🇽 +52 55 3385 1066",
    to: "tel:+525533851066",
    external: true,
    icon: <Phone className="h-3.5 w-3.5 shrink-0 text-ds-violet-pop" aria-hidden="true" />,
  },
  {
    label: "Teléfono Estados Unidos",
    literal: "🇺🇸 +1 (956) 805-1175",
    to: "tel:+19568051175",
    external: true,
    icon: <Phone className="h-3.5 w-3.5 shrink-0 text-ds-violet-pop" aria-hidden="true" />,
  },
];

export const DEFAULT_SOCIALS: readonly FooterSocial[] = [
  { label: "Instagram", href: "https://instagram.com/itsthebest", icon: Instagram },
  { label: "Facebook", href: "https://facebook.com/itsthebest", icon: Facebook },
  { label: "YouTube", href: "https://youtube.com/@itsthebest", icon: Youtube },
  { label: "LinkedIn", href: "https://linkedin.com/company/itsthebest", icon: Linkedin },
];

/**
 * Cuarta columna del pie: cómo hablar con alguien, y las redes.
 *
 * Los teléfonos y el correo NO pasan por `t()`: un número no se traduce.
 * Viajan como `literal`, y lo que sí se traduce es la etiqueta que los
 * describe para el lector de pantalla.
 */
export function FooterContactColumn({
  title,
  channels,
  socials,
}: FooterContactColumnProps) {
  const { t } = useTranslation();

  return (
    <div>
      <h3 className="text-ds-label font-bold uppercase tracking-ds-label text-ds-ink">
        {t(title)}
      </h3>

      <ul className="mt-4 grid gap-2.5">
        {channels.map((channel) => (
          <li key={channel.to}>
            <FooterLink {...channel} />
          </li>
        ))}
      </ul>

      <div className="mt-5 flex flex-wrap items-center gap-2">
        {socials.map(({ label, href, icon: Icon }) => (
          <a
            key={label}
            href={href}
            target="_blank"
            rel="noreferrer"
            aria-label={label}
            className="inline-flex h-8 w-8 items-center justify-center rounded-ds-pill border border-ds-border bg-white text-ds-ink-3 transition hover:border-ds-violet-pop hover:text-ds-violet-pop"
          >
            <Icon className="h-3.5 w-3.5" aria-hidden="true" />
          </a>
        ))}
      </div>
    </div>
  );
}
